import { useCallback, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { USER_QUERY_KEY } from "@/hooks/use-user";
import { useUserSettingsQuery } from "@/hooks/use-user-settings";
import { useServiceStore } from "@/stores/service-store";

declare global {
  interface Window {
    Appio?: ((config: {
      service: string;
      eventCallback: (type: string, data: unknown) => void;
    }) => {
      open: (options: { user: string; service?: string }) => void;
    }) & {
      close: () => void;
    };
  }
}

type AppioInstance = ReturnType<NonNullable<typeof window.Appio>>;

const APPIO_SCRIPT_URL = "https://cdn.appio.so/v1/appio.js";

// window.Appio has no teardown and throws if initialized twice, so the script
// and its instance live for the whole page: callers mounting again (the
// Getting started step reopening) reuse them instead of loading another copy
// and stacking callbacks. The service it was initialized with is only a
// default; every open() names the selected one, which can change in-app.
let scriptPromise: Promise<void> | null = null;
let appio: AppioInstance | null = null;
// The mounted callers' handlers; the single instance forwards its event to them.
const deviceConnectedHandlers = new Set<() => void>();

function loadAppioScript(): Promise<void> {
  scriptPromise ??= new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = APPIO_SCRIPT_URL;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      script.remove();
      scriptPromise = null;
      reject(new Error("Failed to load appio.js"));
    };
    document.body.appendChild(script);
  });
  return scriptPromise;
}

function createAppio(serviceId: string) {
  if (!window.Appio || appio) return;
  appio = window.Appio({
    service: serviceId,
    eventCallback: (type: string) => {
      if (type === "appio.device_connected") {
        deviceConnectedHandlers.forEach((handler) => handler());
        window.Appio?.close();
      }
    },
  });
}

interface UseAddDeviceOptions {
  onDeviceConnected?: () => void;
  /**
   * Load appio.js as soon as the caller mounts, so the first click opens the
   * dialog at once. Off, the script loads on the first click instead (for
   * callers that are always mounted, like the sidebar).
   */
  preload?: boolean;
}

/**
 * The "Add device" flow: opens the appio.js dialog that installs the app on
 * the admin's own phone. `isReady` is false until the user and the selected
 * service are known; until then `addDevice` is a no-op.
 */
export function useAddDevice({
  onDeviceConnected,
  preload = true,
}: UseAddDeviceOptions = {}) {
  const queryClient = useQueryClient();
  const { selectedServiceId } = useServiceStore();
  const { data: userSettings } = useUserSettingsQuery();

  const handleDeviceConnected = useCallback(() => {
    // A new device can complete Getting started steps, which come with the user.
    queryClient.invalidateQueries({ queryKey: USER_QUERY_KEY });
    onDeviceConnected?.();
  }, [onDeviceConnected, queryClient]);

  useEffect(() => {
    deviceConnectedHandlers.add(handleDeviceConnected);
    return () => {
      deviceConnectedHandlers.delete(handleDeviceConnected);
    };
  }, [handleDeviceConnected]);

  useEffect(() => {
    if (!preload || !selectedServiceId) return;
    loadAppioScript()
      .then(() => createAppio(selectedServiceId))
      .catch(() => {
        // addDevice stays a no-op; the next mount or click tries again.
      });
  }, [preload, selectedServiceId]);

  const userId = userSettings?.user_id ? `admin_${userSettings.user_id}` : "";
  const isReady = !!userId && !!selectedServiceId;

  const addDevice = useCallback(() => {
    if (!isReady || !selectedServiceId) return;
    const open = () => {
      createAppio(selectedServiceId);
      appio?.open({ user: userId, service: selectedServiceId });
    };
    if (window.Appio) {
      open();
      return;
    }
    loadAppioScript()
      .then(open)
      .catch(() => {
        // Nothing to open; the next click tries loading again.
      });
  }, [isReady, selectedServiceId, userId]);

  return { addDevice, isReady };
}
